/**
 * Retry Utility with Exponential Backoff
 * 
 * Generic retry mechanism for async operations with:
 * - Exponential backoff
 * - Configurable retry strategies
 * - Operation-specific configurations
 * - Error filtering (only retry specific errors)
 * - Timeout handling
 * - Structured logging
 * 
 * @module utilities/pinpad-v2/retry
 */

import {
  getRetryConfig,
  getRetryStrategy,
  calculateRetryDelay,
  shouldRetry as shouldRetryByAttempt,
  isErrorRetryable
} from '../../config/pinpad-v2/retry-config';

/**
 * Sleep utility for retry delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute function with timeout
 * @param {Function} fn - Async function to execute
 * @param {number} timeout - Timeout in milliseconds
 * @param {string} operation - Operation name for error messages
 * @returns {Promise<any>} Function result
 * @throws {Error} Timeout error
 */
async function executeWithTimeout(fn, timeout, operation) {
  return Promise.race([
    fn(),
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error(`Operation ${operation} timed out after ${timeout}ms`)),
        timeout
      )
    )
  ]);
}

/**
 * Retry configuration options
 * @typedef {Object} RetryOptions
 * @property {string} operation - Operation name (CONNECT, LOGIN, VENTA, etc.)
 * @property {Function} onRetry - Callback called before each retry (attempt, error, delay)
 * @property {Function} shouldRetry - Custom retry condition (error) => boolean
 * @property {Object} context - Additional context for logging
 */

/**
 * Execute async operation with retry logic
 * 
 * @param {Function} fn - Async function to execute
 * @param {RetryOptions} options - Retry configuration options
 * @returns {Promise<any>} Function result
 * @throws {Error} Last error if all retries fail
 */
export async function withRetry(fn, options = {}) {
  const {
    operation = 'UNKNOWN',
    onRetry = null,
    shouldRetry: customShouldRetry = null,
    context = {}
  } = options;

  // Get retry configuration for this operation
  const retryConfig = getRetryConfig(operation);
  const strategy = retryConfig.strategy;
  const timeout = retryConfig.timeout;

  let lastError = null;
  let attempt = 0;

  // Main retry loop
  while (attempt <= strategy.maxRetries) {
    try {
      // Execute function with timeout
      const result = await executeWithTimeout(fn, timeout, operation);
      
      // Success - return result
      if (attempt > 0) {
        console.info(`[Retry] Operation ${operation} succeeded after ${attempt} retries`);
      }
      
      return result;

    } catch (error) {
      lastError = error;
      
      // Check if we should retry
      const isLastAttempt = attempt >= strategy.maxRetries;
      const errorCode = error.code || '9999';
      
      // Determine if error is retryable
      const isRetryableError = customShouldRetry
        ? customShouldRetry(error)
        : isErrorRetryable(operation, errorCode);

      // Don't retry if:
      // - Last attempt reached
      // - Error is not retryable
      // - Custom shouldRetry returns false
      if (isLastAttempt || !isRetryableError) {
        console.error(
          `[Retry] Operation ${operation} failed permanently after ${attempt} attempts`,
          {
            operation,
            attempt,
            errorCode,
            errorMessage: error.message,
            context
          }
        );
        throw error;
      }

      // Calculate retry delay
      const delay = calculateRetryDelay(attempt, strategy);
      
      console.warn(
        `[Retry] Operation ${operation} failed (attempt ${attempt + 1}/${strategy.maxRetries + 1}). ` +
        `Retrying in ${delay}ms...`,
        {
          operation,
          attempt: attempt + 1,
          errorCode,
          errorMessage: error.message,
          delay,
          context
        }
      );

      // Call onRetry callback if provided
      if (onRetry) {
        try {
          await onRetry(attempt, error, delay);
        } catch (callbackError) {
          console.error('[Retry] onRetry callback failed:', callbackError);
        }
      }

      // Wait before retry
      await sleep(delay);
      
      attempt++;
    }
  }

  // Should never reach here, but just in case
  throw lastError || new Error(`Operation ${operation} failed with unknown error`);
}

/**
 * Create a retryable version of an async function
 * Useful for wrapping functions that should always retry
 * 
 * @param {Function} fn - Async function to wrap
 * @param {RetryOptions} options - Retry configuration options
 * @returns {Function} Wrapped function with retry logic
 */
export function retryable(fn, options = {}) {
  return async (...args) => {
    return withRetry(() => fn(...args), options);
  };
}

/**
 * Execute multiple operations with retry logic in sequence
 * Stops on first failure
 * 
 * @param {Array<Object>} operations - Array of {fn, options}
 * @returns {Promise<Array>} Array of results
 */
export async function withRetrySequence(operations) {
  const results = [];
  
  for (const { fn, options } of operations) {
    const result = await withRetry(fn, options);
    results.push(result);
  }
  
  return results;
}

/**
 * Execute multiple operations with retry logic in parallel
 * All operations execute independently
 * 
 * @param {Array<Object>} operations - Array of {fn, options}
 * @returns {Promise<Array>} Array of results (or errors)
 */
export async function withRetryParallel(operations) {
  const promises = operations.map(({ fn, options }) =>
    withRetry(fn, options).catch(error => ({ error }))
  );
  
  return Promise.all(promises);
}

/**
 * Circuit breaker state
 */
class CircuitBreaker {
  constructor(options = {}) {
    this.failureThreshold = options.failureThreshold || 5;
    this.resetTimeout = options.resetTimeout || 60000; // 1 minute
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.lastFailureTime = null;
  }

  async execute(fn, options = {}) {
    // Check circuit state
    if (this.state === 'OPEN') {
      // Check if should transition to HALF_OPEN
      if (Date.now() - this.lastFailureTime > this.resetTimeout) {
        console.info('[CircuitBreaker] Transitioning to HALF_OPEN state');
        this.state = 'HALF_OPEN';
      } else {
        const error = new Error('Circuit breaker is OPEN');
        error.code = '0402';
        throw error;
      }
    }

    try {
      const result = await withRetry(fn, options);
      
      // Success - reset circuit
      if (this.state === 'HALF_OPEN') {
        console.info('[CircuitBreaker] Operation succeeded, closing circuit');
        this.reset();
      }
      
      return result;

    } catch (error) {
      this.recordFailure();
      throw error;
    }
  }

  recordFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.failureCount >= this.failureThreshold) {
      console.error(
        `[CircuitBreaker] Failure threshold reached (${this.failureCount}), opening circuit`
      );
      this.state = 'OPEN';
    }
  }

  reset() {
    this.failureCount = 0;
    this.state = 'CLOSED';
    this.lastFailureTime = null;
  }

  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime
    };
  }
}

/**
 * Create a circuit breaker instance
 * @param {Object} options - Circuit breaker options
 * @returns {CircuitBreaker} Circuit breaker instance
 */
export function createCircuitBreaker(options = {}) {
  return new CircuitBreaker(options);
}

export default {
  withRetry,
  retryable,
  withRetrySequence,
  withRetryParallel,
  createCircuitBreaker
};
